import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import EmployeeForm from './EmployeeForm';
import AddressCompact from '../Address/AddressCompact';
import ContactInfoCompact from '../ContactInfo/Compact/ContactInfoCompact';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import { CreateEmployeeCommand, UpdateEmployeeCommand, SupervisorDTO, ListOptionDTOSearchResponseResponse, ListOptionDTO } from '../../axiosApi/models';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { ButtonWithOptions } from "components/common/ButtonWithOptions";
import { useDialog } from 'common/DialogProvider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import { PermissionsGate } from 'components/authorization/PermissionsGate';
import RequiredField from '../common/RequiredField';
import intl from 'components/i18n/ReactIntlWrapper';
import Spinner from '../Spinner';
import CurrentAssignations from './CurrentAssignations/CurrentAssignations';
import { handleAPIError } from 'common/errorHandler';
import BillingTable from 'components/Billings/BillingTable';
import { ENTITY_TYPE } from 'common/constants';
import TimeOffSection from './TimeOffSection';
import EntityInvoiceList from 'components/Invoicing/EntityInvoiceList';
import ProfilesSection from 'components/HumanResources/Profiles/ProfilesSection';
import {
    Typography,
    Box,
    Button,
    Stack,
    MenuItem,
    Card
  } from '@mui/material';
import { AutocompleteCustomProps } from 'components/common/TypeAhead';


export type EmployeeDetailInput = {
    name?: string | null,
    lastName?: string | null,
    emailAddress?: string | null,
    isActive?: boolean,
    idAssignedSupervisor?: number | null,
    isSupervisor?: boolean
};

const EmployeeDetail = () => {

    const [input, setInput] = useState<EmployeeDetailInput | null>({
        name: "",
        lastName: "",
        emailAddress: "",
        isActive: true,
        idAssignedSupervisor: null,
        isSupervisor: false
    });

    const api: Api = useApi();

    const navigate = useNavigate();

    const toast = useToastMessageQueue();

    const { openDialog } = useDialog();

    const { id } = useParams();
    const idNumber = parseInt(id);

    const [addressesId, setAddressesId] = useState<number[]>([]);

    const [contactInfosId, setContactInfosId] = useState<number[]>([]);

    const [personaExist, setPersonaExist] = useState<boolean>(false);

    const [errors, setErrors] = useState({});

    const [disabled, setDisabled] = useState(false);

    const [disabledNew, setDisabledNew] = useState(false);

    const [loading, setLoading] = useState<boolean>(false);

    const [supervisors, setSupervisors] = useState<SupervisorDTO[]>([]);

    const [provider, setProvider] = useState<ListOptionDTO>(null);

    const fetchSupervisors = async () => {
        try {
            const response = await api.employeeApi.apiVversionEmployeeSupervisorsGet("1", undefined, false);
            if (response.data?.data) {
                setSupervisors(response.data.data);
            }
        } catch (error) {
            handleAPIError(error, toast, errors);
            setErrors({ ...errors });
        }
    };

    const fetchEmployeeDetail = async (idNumber: number) => {
        setLoading(true);
        const res = await api.employeeApi.apiVversionEmployeeIdGet(idNumber, "1", true).then((response) => {
            if (response.data) {
                setInput({
                  name: response.data?.data?.employee?.name,
                  lastName: response.data?.data?.employee?.lastName,
                  emailAddress: response.data?.data?.employee?.emailAddress,
                  isActive: response.data?.data?.employee?.isActive,
                  idAssignedSupervisor: (response.data?.data?.employee as any)?.idAssignedSupervisor || null,
                  isSupervisor: (response.data?.data?.employee as any)?.isSupervisor || false
                })
                setAddressesId(response.data?.data?.addressIds);
                setContactInfosId(response.data?.data?.contactInfoIds);
                if (response.data?.data?.employee?.provider) {
                  const currentProvider = response.data.data.employee.provider;
                  setProvider({
                    id: currentProvider.id,
                    label: currentProvider.name
                  });
                }
            };
            setLoading(false);
        }).catch((error) => {
            handleAPIError(error, toast, errors);
            setErrors({ ...errors });
            setLoading(false);
        });
    };

    useEffect(() => {
        fetchSupervisors();
        if (idNumber) {
            fetchEmployeeDetail(idNumber);
            setDisabled(true);
        };
        setDisabledNew(true);
    }, [idNumber]);

    const handleInputChange = function (e) {
        setInput({
            ...input,
            [e.target.name]: e.target.value
        });
        setErrors(validate({
            ...input,
            [e.target.name]: e.target.value
        }));
    };

    const handleStatusChange = (e) => {
        const newStatus = e.target.checked;
        if (!newStatus && input?.isActive) {
            openDialog({
                type: 'confirm',
                title: intl.get('employeeDetail.deactivate.title') || 'Deactivate Employee',
                message: intl.get('employeeDetail.deactivate.message') || 'Are you sure you want to deactivate this employee? They will no longer appear in the active employee list.',
                confirmButton: {
                    confirmLabel: intl.get('common.confirm') || 'Confirm',
                    handleConfirm: (message) => {
                        setInput({
                            ...input,
                            isActive: newStatus
                        });
                        message.handleClose(message);
                    }
                },
                cancelButton: {
                    cancelLabel: intl.get('cancel.button') || 'Cancel',
                    handleCancel: (message) => {
                        message.handleClose(message);
                    }
                }
            });
        } else {
            // If activating, no confirmation needed
            setInput({
                ...input,
                isActive: newStatus
            });
        }
    };

    const handleSupervisorChange = (event, newValue) => {
        setInput({
            ...input,
            idAssignedSupervisor: newValue ? newValue.id : null
        });
    };

    const handleIsSupervisorChange = (e) => {
        setInput({
            ...input,
            isSupervisor: e.target.checked
        });
    };

    const validate = function (input: EmployeeDetailInput) {
        setDisabledNew(false);
        let errors: any = {};
        if (!input.name) {
            errors.name = intl.get('validate.errors.isRequired');
        }
        if (!input.lastName) {
            errors.lastName = intl.get('validate.errors.isRequired');
        }
        if (!input.emailAddress) {
            errors.emailAddress = intl.get('validate.errors.isRequired');
        } else if (!/\S+@\S+\.\S+/.test(input.emailAddress)) {
            errors.emailAddress = intl.get('validate.errors.isInvalid');
        }
        return errors;
    };

    const handleSubmit = async (saveAndEdit: boolean = false) => {
        setLoading(true);
        const errors = validate(input);
        setErrors(errors);
        if (Object.keys(errors).length === 0) {
            if (idNumber) {
                let cmd: any = {
                    name: input.name,
                    lastName: input.lastName,
                    emailAddress: input.emailAddress,
                    id: idNumber,
                    addressIds: addressesId,
                    contactInfoIds: contactInfosId,
                    isActive: input.isActive,
                    idAssignedSupervisor: input.idAssignedSupervisor,
                    isSupervisor: input.isSupervisor,
                    idProvider: provider ? provider.id : null
                };
                try {
                    await api.employeeApi.apiVversionEmployeePut("1", cmd, {});
                    if (saveAndEdit)
                        setDisabled(true);
                    else
                        navigate('/employees');
                    toast.success({ message: intl.get('employeeDetail.toast.success.handleSubmit')});
                    setLoading(false);
                } catch (error) {
                  setDisabled(true);
                    handleAPIError(error, toast, errors);
                    setErrors({ ...errors });
                    setLoading(false);
                }
            } else {
                let cmd: CreateEmployeeCommand = {
                    name: input.name,
                    lastName: input.lastName,
                    emailAddress: input.emailAddress,
                    idAssignedSupervisor: input.idAssignedSupervisor,
                    isSupervisor: input.isSupervisor,
                    isActive: input.isActive,
                    idProvider: provider ? provider.id : null
                };
                try {
                    const response = await api.employeeApi.apiVversionEmployeePost("1", cmd, {});
                    toast.success({ message: intl.get('employeeDetail.toast.success.handleSubmit.add')});
                    setLoading(false);
                    if (saveAndEdit)
                        navigate(`/employee/detail/${response.data.data.id}`);
                    else
                        navigate('/employees');
                } catch (error) {
                    handleAPIError(error, toast, errors);
                    setErrors({ ...errors });
                    setLoading(false);
                }
            };
        }
    };

    const handleBackClick = (e) => {
        navigate(-1);
    };

    const handleSaveClick = () => {
        handleSubmit(true);
    };

    const handleSaveCloseClick = () => {
        handleSubmit(false);
    };

    const handleEditMode = (e) => {
        e.preventDefault();
        if (idNumber) {
            fetchEmployeeDetail(idNumber);
        }
        setErrors({});
        setDisabled(!disabled);
    };

    const providerTypeaheadProps: AutocompleteCustomProps<ListOptionDTO, undefined, undefined, undefined> = {
      apiPromise: (searchTerm: string) => {
        return api.employeeApi.apiVversionListProvidersTypeAheadGet("1", searchTerm)
          .then((response) => {
            return response.data.data.queryResult || [];
          })
      },
      label: intl.get('employeeForm.provider'),
      getOptionLabel: (option: any) => option.label,
      onChange: (_event: React.SyntheticEvent, selectedOption: ListOptionDTO | null) => {
        setProvider(selectedOption);
      },
      value: provider,
      disableClearable: false,
      disabled
    };

    return (
      <Box className='my-3' sx={{ width: '100%', p: 2 }} >
        <Card sx={{ p: 2, mb: 3 }}>
          <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
            {idNumber ? intl.get('employeeDetail.header') : intl.get('newEmployee.header')}
          </Typography>
          <EmployeeForm
            errors={errors}
            input={input}
            handleInputChange={handleInputChange}
            handleStatusChange={handleStatusChange}
            disabled={disabled}
            supervisors={supervisors}
            handleSupervisorChange={handleSupervisorChange}
            handleIsSupervisorChange={handleIsSupervisorChange}
            providerTypeaheadProps={providerTypeaheadProps}
          />
          <RequiredField />
        </Card>

              {!!idNumber && (
                <>
                      <Card sx={{ mb: 3, p: 2 }}>
                        <BillingTable
                          entityId={idNumber}
                          idEntityType={ENTITY_TYPE.EMPLOYEE}
                          disabled={disabled}
                        />
                      </Card>
                      
                      <Card sx={{ mb: 3, p: 2 }}>
                        <AddressCompact
                          addressesId={addressesId}
                          setAddressesId={setAddressesId}
                          personaExist={personaExist}
                          disabled={disabled}
                          identifier="ui.employees.detail.info-form"
                        />
                      </Card>

                      <Card sx={{ mb: 3, p: 2 }}>
                        <ContactInfoCompact
                          contactInfosId={contactInfosId}
                          setContactInfosId={setContactInfosId}
                          disabled={disabled}
                          identifier="ui.employees.detail.assignment-form"
                        />
                      </Card>
                      
                      <Card sx={{ mb: 3, p: 2 }}>
                        <CurrentAssignations idEmployee={idNumber} />
                      </Card>
                      
                      <Card sx={{ mb: 3, p: 2 }}>
                        <TimeOffSection employeeId={idNumber} disabled={disabled} />
                      </Card>

                      <Card sx={{ mb: 3, p: 2 }}>
                        <ProfilesSection
                          employeeId={idNumber}
                          employeeName={input.name}
                          employeeLastName={input.lastName}
                        />
                      </Card>

                      <Card sx={{ mb: 3, p: 2 }}>
                        <EntityInvoiceList
                          entityId={idNumber}
                          entityTypeId={ENTITY_TYPE.EMPLOYEE}
                          issuedByEntity={true}
                          workflowModuleId={4}
                        />
                      </Card>
                </>
              )}

              <Box display="flex" justifyContent="flex-end" mt={3}>
                {(!idNumber && !disabled) && (
                  loading ? (
                    <Spinner small={true} />
                  ) : (
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="outlined"
                        color="inherit"
                        onClick={handleBackClick}
                      >
                        <ArrowBackIcon />
                      </Button>

                      <PermissionsGate
                        identifier="ui.employees.detail.info-form"
                        viewRoles={[]}
                        editRoles={[]}
                      >
                        <ButtonWithOptions
                          menu={<>
                              <MenuItem onClick={handleSaveCloseClick}>{intl.get('save&close.button')}</MenuItem>
                          </>}
                          disabled={Object.keys(errors).length > 0 || disabledNew}
                          className="btn btn-primary"
                          onClick={handleSaveClick}
                        >
                          <SaveIcon className='mb-1' />
                          {intl.get('save.button')}
                        </ButtonWithOptions>
                      </PermissionsGate>
                    </Stack>
                  )
                )}
                {(!isNaN(idNumber) && disabled) && (
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={handleBackClick}
                    >
                      <ArrowBackIcon />
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleEditMode}
                      startIcon={<EditIcon />}
                    >
                      {intl.get('edit.button')}
                    </Button>
                  </Stack>
                )}

                {(!isNaN(idNumber) && !disabled) && (
                  loading ? (
                    <Spinner small={true} />
                  ) : (
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="outlined"
                        color="inherit"
                        onClick={handleEditMode}
                      >
                        {intl.get('cancel.button')}
                      </Button>
                      <PermissionsGate
                        identifier="ui.employees.detail.assignment-form"
                        viewRoles={[]}
                        editRoles={[]}
                      >
                        <ButtonWithOptions
                          menu={<>
                              <MenuItem onClick={handleSaveCloseClick}>{intl.get('save&close.button')}</MenuItem>
                          </>}
                          disabled={Object.keys(errors).length > 0 || disabled}
                          className="btn btn-primary"
                          onClick={handleSaveClick}
                        >
                          <SaveIcon className='mb-1' />
                          {intl.get('save.button')}
                        </ButtonWithOptions>
                      </PermissionsGate>
                    </Stack>
                  )
                )}
              </Box>
        </Box>
    )
}

export default EmployeeDetail;

